import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useUpdateChatSettingsDataMutationMutation,
  ChatUpdateInput,
} from '../../../generated';

interface UseUpdateChatSettingsConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UpdateChatSettingsParams {
  chatId: string;
  chat: ChatUpdateInput;
}

export const useUpdateChatSettings = (
  config: UseUpdateChatSettingsConfig = {}
) => {
  const { onSuccess, onError } = config;
  const { addNotification } = useNotification();

  const [updateChatSettings, { loading: isUpdating }] =
    useUpdateChatSettingsDataMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onCompleted: () => {
        addNotification({
          title: 'Success',
          message: 'Chat settings updated successfully.',
          type: 'success',
        });
        onSuccess?.();
      },
      onError: (apolloError) => {
        const errorMessage =
          apolloError.message || 'Failed to update chat settings';
        onError?.(new Error(errorMessage));
        addNotification({
          title: 'Error',
          message: errorMessage,
          type: 'error',
        });
      },
    });

  const updateSettings = useCallback(
    async ({ chatId, chat }: UpdateChatSettingsParams) => {
      try {
        await updateChatSettings({
          variables: {
            id: chatId,
            chat,
          },
        });
      } catch (error) {
        // Error is already handled by onError callback
        // Error logged via notification system
      }
    },
    [updateChatSettings]
  );

  return {
    updateSettings,
    isUpdating,
  };
};
